import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type {
  AtHomeAccountDetail,
  AtHomeAlerts,
  AtHomeCaseload,
  AtHomeFleet,
  AtHomeMonitoring,
  AtHomeOutcome,
  AtHomePanels,
  AtHomeReimbursements,
  AtHomeRevenue,
  ConciergeKind,
  ConciergeResponse,
  DemoHousehold,
  HomeActionBody,
  HomeActionResponse,
  HomeBenefits,
  HomeCaregivingDashboard,
  HomeCognitive,
  HomeMedications,
  HomeReminders,
  HomeResidentScore,
  HomeEmergency,
  HomePeople,
  HomeToday,
  HomeWellbeing,
  ResidentHome,
} from './home-types';

const A = AITHORA_PREFIX;

// ── REAL (served today) ─────────────────────────────────────────────────────────

/** GET /athome/caseload: the Operator's at-home members (requireStaff, session org). */
export const atHomeCaseload = route<AtHomeCaseload>()('GET', `${A}/athome/caseload`);

/** GET /athome/me/:id: one member's landing (self, or staff of their org). */
export const atHomeMe = route<ResidentHome>()('GET', `${A}/athome/me/:id`);

/** GET /home/demo-household: the badged SAMPLE household for a preview. */
export const homeDemoHousehold = route<DemoHousehold>()('GET', `${A}/home/demo-household`);

/** POST /home/:residentId/concierge: a request for a hand (ride, refill, visit, meal, other). */
export const homeConcierge = route<ConciergeResponse, void, { organizationId?: string; kind: ConciergeKind; note?: string | null }>()(
  'POST', `${A}/home/:residentId/concierge`);

/** GET /residents/:id/score: the score behind "How you are holding up". */
export const homeResidentScore = route<HomeResidentScore>()('GET', `${A}/residents/:id/score`);
/** GET /cognitive?userId=: the memory checks. */
export const homeCognitive = route<HomeCognitive, { userId: string }>()('GET', `${A}/cognitive`);
/** GET /medications?userId=&organizationId= */
export const homeMedications = route<HomeMedications, { userId: string; organizationId: string }>()('GET', `${A}/medications`);
/** GET /reminders?residentId=: what is coming up. */
export const homeReminders = route<HomeReminders, { residentId: string }>()('GET', `${A}/reminders`);
/** GET /home/caregiving-dashboard?residentId=: Atlas's highlights. */
export const homeCaregivingDashboard = route<HomeCaregivingDashboard, { residentId: string }>()('GET', `${A}/home/caregiving-dashboard`);

// ── Served by src/aithora/v3/home/ (Console V3 people pages) ─────────────────────

/** GET /athome/me/:id/today: check-in, the week's readings, coming up, noticed. */
export const homeToday = route<HomeToday>()('GET', `${A}/athome/me/:id/today`, { honest: true });
/** GET /athome/me/:id/wellbeing: the My home tab. */
export const homeWellbeing = route<HomeWellbeing>()('GET', `${A}/athome/me/:id/wellbeing`, { honest: true });
/** GET /athome/me/:id/benefits */
export const homeBenefits = route<HomeBenefits>()('GET', `${A}/athome/me/:id/benefits`, { honest: true });
/** GET /athome/me/:id/emergency: the ladder and planning. */
export const homeEmergency = route<HomeEmergency>()('GET', `${A}/athome/me/:id/emergency`, { honest: true });
/** GET /athome/me/:id/people: the care circle. */
export const homePeople = route<HomePeople>()('GET', `${A}/athome/me/:id/people`, { honest: true });
/** POST /athome/me/:id/actions: every write a member makes from Home. */
export const homeAction = route<HomeActionResponse, void, HomeActionBody>()('POST', `${A}/athome/me/:id/actions`, { honest: true });

/** GET /athome/caseload/panels */
export const atHomePanels = route<AtHomePanels>()('GET', `${A}/athome/caseload/panels`, { honest: true });
/** GET /athome/alerts */
export const atHomeAlerts = route<AtHomeAlerts>()('GET', `${A}/athome/alerts`, { honest: true });
/** POST /athome/alerts/:id/claim */
export const atHomeClaimAlert = route<{ real: boolean; owner: string }>()('POST', `${A}/athome/alerts/:id/claim`, { honest: true });
/** POST /athome/alerts/:id/close {outcome, note} */
export const atHomeCloseAlert = route<{ real: boolean }, void, { outcome: AtHomeOutcome; note: string }>()('POST', `${A}/athome/alerts/:id/close`, { honest: true });
/** GET /athome/fleet */
export const atHomeFleet = route<AtHomeFleet>()('GET', `${A}/athome/fleet`, { honest: true });
/** GET /athome/revenue */
export const atHomeRevenue = route<AtHomeRevenue>()('GET', `${A}/athome/revenue`, { honest: true });
/** GET /athome/monitoring */
export const atHomeMonitoring = route<AtHomeMonitoring>()('GET', `${A}/athome/monitoring`, { honest: true });
/** GET /athome/reimbursements */
export const atHomeReimbursements = route<AtHomeReimbursements>()('GET', `${A}/athome/reimbursements`, { honest: true });
/** POST /athome/reimbursements/:id/file */
export const atHomeFileFinding = route<{ real: boolean; state: 'FILED' }>()('POST', `${A}/athome/reimbursements/:id/file`, { honest: true });
/** GET /athome/members/:id/account */
export const atHomeAccount = route<AtHomeAccountDetail>()('GET', `${A}/athome/members/:id/account`, { honest: true });
/** POST /athome/members/:id/account-actions {action, undo?, confirm?} */
export const atHomeAccountAction = route<{ real: boolean; done: string }, void, { action: string; undo?: boolean; confirm?: boolean }>()(
  'POST', `${A}/athome/members/:id/account-actions`, { honest: true });
